import React, { useContext, useRef, useState } from 'react';
import { Heading, RowWithSpaceBetween } from '@components/styles.tsx';
import { ApiContext } from '@api/api';
import RingilDataTable, { ColumnConfig, PagedData, FilterConfig, FilterType, MassActionConfig } from '@components/RingilDataTable/RingilDataTable.tsx';
import {
  Pageable,
  ListShippingCompaniesItemResponseOutDTO,
  ListShippingCompaniesFilter,
  PageResponseListShippingCompaniesItemResponseOutDTO,
} from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilMoreButtons, { MenuSection } from '@app/pages/RingilMoreButtons/RingilMoreButtons';
import SvgEdit from '@components/ringil3/icons/buttons/Edit.tsx';
import SvgDelete from '@components/ringil3/icons/buttons/Delete.tsx';
import SvgDeleteTrash from '@icons/delete-trash.svg?react';
import { useAddEditShipCompanySidebar } from './AddEditShipCompanySidebar.tsx';

const AllShipCompanies: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { adminListShippingCompanies, adminDeleteShippingCompany } = useContext(ApiContext);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: ListShippingCompaniesItemResponseOutDTO[]; clear: () => void }>(null);

  const [selectedCompany, setSelectedCompany] = useState<ListShippingCompaniesItemResponseOutDTO | null>(null);

  const { setVisible: setVisibleAddEditSidebar, AddEditShipCompanySidebar } = useAddEditShipCompanySidebar();

  const reloadTable = () => {
    tableRef.current?.reloadData();
  };

  const getAllPositions = (callback: (positions: number[]) => void) => {
    // Fetch all positions to validate uniqueness
    adminListShippingCompanies(
      { pageNumber: 1, pageSize: 10000 }, // Large page size to get all items
      { showDeleted: false }, // Only active items
      (response) => {
        const positions = response.data.map(item => item.position);
        callback(positions);
      }
    );
  };

  const handleDelete = (id: string) => {
    adminDeleteShippingCompany(id, () => {
      reloadTable();
    });
  };

  const handleMassDelete = (selectedItems: ListShippingCompaniesItemResponseOutDTO[]) => {
    // Delete all selected items sequentially
    let completed = 0;
    const total = selectedItems.length;

    selectedItems.forEach((item) => {
      adminDeleteShippingCompany(item.id, () => {
        completed++;
        // Reload table after all deletions are complete
        if (completed === total) {
          tableRef.current?.clear();
          reloadTable();
        }
      });
    });
  };

  const loadDataApi = (
    filter: ListShippingCompaniesFilter,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<ListShippingCompaniesItemResponseOutDTO>) => void
  ) => {
    adminListShippingCompanies(
      pageable,
      filter,
      (response: PageResponseListShippingCompaniesItemResponseOutDTO) => {
        onSuccess({
          data: response.data,
          total: response.total,
          page: response.page,
          size: response.size,
        });
      }
    );
  };

  const renderIdColumn = (row: ListShippingCompaniesItemResponseOutDTO) => {
    return <div>{row.id}</div>;
  };

  const renderNameColumn = (row: ListShippingCompaniesItemResponseOutDTO) => {
    return <div>{row.name}</div>;
  };

  const renderPositionColumn = (row: ListShippingCompaniesItemResponseOutDTO) => {
    return <div>{row.position}</div>;
  };

  const renderStatusColumn = (row: ListShippingCompaniesItemResponseOutDTO) => {
    return (
      <div>
        {!row.deleted ? (
          <span className="text-green-600">{tr('ShipCompanies.active', 'Active')}</span>
        ) : (
          <span className="text-red-600">{tr('ShipCompanies.deleted', 'Deleted')}</span>
        )}
      </div>
    );
  };

  const renderActionsColumn = (row: ListShippingCompaniesItemResponseOutDTO) => {
    const menuSections: MenuSection[] = [
      {
        id: 'actions',
        title: 'Actions',
        titleKey: 'ShipCompanies.actionsSection',
        items: [
          {
            id: 'edit',
            label: tr('ShipCompanies.edit', 'Edit'),
            icon: <SvgEdit />,
            onClick: () => {
              setSelectedCompany(row);
              setVisibleAddEditSidebar(true);
            },
            visible: true,
          },
          {
            id: 'delete',
            label: tr('ShipCompanies.delete', 'Delete'),
            icon: <SvgDelete />,
            onClick: () => {
              handleDelete(row.id);
            },
            visible: true,
            className: 'text-red-600 hover:text-red-800',
          },
        ],
      },
    ];

    return (
      <RingilMoreButtons
        sections={menuSections}
        buttonColor="#616061"
        menuPosition="right"
        onItemClick={(itemId) => {
          console.log(`Shipping company action: ${itemId} for ${row.id}`);
        }}
      />
    );
  };

  const columnsConfig: ColumnConfig<ListShippingCompaniesItemResponseOutDTO>[] = [
    {
      id: 'id',
      header: 'ShipCompanies.columnHeaderId',
      headerDefault: 'ID',
      width: '25%',
      sortable: true,
      field: 'id',
      order: 1,
      render: renderIdColumn,
    },
    {
      id: 'name',
      header: 'ShipCompanies.columnHeaderName',
      headerDefault: 'Name',
      width: '45%',
      sortable: true,
      field: 'name',
      order: 2,
      render: renderNameColumn,
    },
    {
      id: 'position',
      header: 'ShipCompanies.columnHeaderPosition',
      headerDefault: 'Position',
      width: '10%',
      sortable: true,
      isDefaultSort: true,
      field: 'position',
      order: 3,
      render: renderPositionColumn,
    },
    {
      id: 'status',
      header: 'ShipCompanies.columnHeaderStatus',
      headerDefault: 'Status',
      width: '10%',
      sortable: true,
      field: 'deleted',
      order: 4,
      render: renderStatusColumn,
    },
    {
      id: 'actions',
      header: 'ShipCompanies.columnHeaderActions',
      headerDefault: 'Actions',
      width: '10%',
      sortable: false,
      order: 5,
      render: renderActionsColumn,
    },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr('ShipCompanies.filterByName', 'Filter by name'),
      value: ['name'],
    },
    {
      filterType: FilterType.Checkbox,
      placeholder: tr('ShipCompanies.showDeleted', 'Show deleted'),
      value: ['showDeleted'],
    },
  ];

  const massActionConfig: MassActionConfig<ListShippingCompaniesItemResponseOutDTO>[] = [
    {
      label: tr('ShipCompanies.deleteSelected', 'Delete selected'),
      icon: <SvgDeleteTrash />,
      onClick: () => {
        const selectedItems = tableRef.current?.selectedObjects || [];
        if (selectedItems.length > 0) {
          handleMassDelete(selectedItems);
        }
      },
      visible: (selected) => selected.length > 0,
    },
  ];

  return (
    <>
      <RowWithSpaceBetween>
        <Heading>{tr('ShipCompanies.title', 'Ship Companies')}</Heading>
        <RingilButton
          label={tr('ShipCompanies.create', 'Create')}
          onClick={() => {
            setSelectedCompany(null);
            setVisibleAddEditSidebar(true);
          }}
        />
      </RowWithSpaceBetween>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig}
        loadDataFromApi={loadDataApi}
        filterName={'admin_shipping_companies_list'}
        filtersConf={filtersConf}
        massActionConfig={massActionConfig}
        floatingMassActions={true}
      />

      <AddEditShipCompanySidebar
        data={selectedCompany}
        getAllPositions={getAllPositions}
        onChanged={() => {
          setSelectedCompany(null);
          reloadTable();
        }}
      />
    </>
  );
};

export default AllShipCompanies;

